<template>
  <div>
    <div class="row full-width">
      <SelectList
        v-model="selectedValue"
        v-bind="$attrs"
        :class="cssClasses"
        clearable
        :dense="dense"
        :display-value="displayValue"
        emit-value
        :label="label"
        map-options
        :multiple="multiple"
        option-label="label"
        option-value="id"
        :options="filterSets"
      />
      <QIcon
        v-if="!hideInfo"
        class="col-1"
        :color="multipleFiltersSelected ? 'radio' : 'primary'"
        name="info"
        size="md"
      >
        <StreemTooltip
          anchor="center end"
          self="center left"
        >
          <div class="q-pa-md">
            <p>
              Multiple filter sets can be chosen for a single brief. If more
              than one filter set is chosen they will combine the same as a
              boolean 'OR' operator. It will include all media that isn't
              filtered out between multiple filters
            </p>
            <p>Example 1</p>
            <ul>
              <li>Filter set A - Only Australia</li>
              <li>Filter set B - Only New Zealand</li>
            </ul>

            <p>
              The brief will contain mentions from both Australia and New
              Zealand.
            </p>

            <p>Example 2</p>
            <ul>
              <li>Filter set A - No sources with the National category</li>
              <li>Filter set B - Only the AFR</li>
            </ul>

            <p>
              AFR is a source with the 'National' category. Filter A will have
              it not be included, while B will have it included. If the brief
              has the above two filter sets applied the AFR will appear.
            </p>

            <p>Example 3</p>
            <ul>
              <li>Filter set A - All Media excluded</li>
              <li>Filter set B - All Media included</li>
            </ul>
            <p>
              If the brief has the above two filter sets applied all Media will
              be included in the brief.
            </p>
          </div>
        </StreemTooltip>
      </QIcon>
      <RestrictedRouterLink
        class="q-pa-md"
        target="_blank"
        :to="newFilterSetLink"
      >
        <QBtn
          v-if="showNewFilterSetLink"
          class="col-1"
          dense
          flat
          icon="add"
          size="md"
        >
          <StreemTooltip> Add new filter set </StreemTooltip>
        </QBtn>
      </RestrictedRouterLink>
    </div>
    <div class="row">
      <QBtn
        v-if="showNewFilterSetLink"
        flat
        label="Refresh filter sets"
        @click="loadFilterSets"
      />
    </div>
    <table
      v-if="multiple && filterSets && selectedValue"
      class="q-table q-mt-md full-width"
    >
      <tbody>
        <tr
          v-for="filterSet in selectedFilterSets"
          :key="filterSet.id"
        >
          <td>{{ filterSet.label }}</td>
          <td class="text-right">
            <QBtn
              flat
              icon="ion-eye"
              @click="viewFilterSet(filterSet)"
            >
              <StreemTooltip>View Filter Set</StreemTooltip>
            </QBtn>
            <QBtn
              flat
              icon="delete"
              @click="removeFilterSet(filterSet)"
            />
          </td>
        </tr>
      </tbody>
    </table>
  </div>
</template>

<script>
import { storeToRefs } from "pinia";

import Organisation from "shared/resources/Organisation";

import SelectList from "src/components/common/SelectList.vue";
import { useUserStore } from "src/stores/user";

export default {
  name: "FilterSetSelector",
  components: {
    SelectList,
  },
  props: {
    modelValue: {
      type: [String, Number, Array],
      default: null,
    },
    label: {
      type: [String],
      default: "",
    },
    organisationId: {
      type: [String, Number],
      required: true,
    },
    dense: {
      type: Boolean,
    },
    multiple: {
      type: Boolean,
    },
    showNewFilterSetLink: {
      type: Boolean,
    },
    hideInfo: {
      type: Boolean,
    },
  },
  emits: ["update:modelValue"],
  setup() {
    const userStore = useUserStore();
    const { organisation } = storeToRefs(userStore);

    return { organisation };
  },
  data() {
    return {
      filterSets: [],
    };
  },
  computed: {
    selectedValue: {
      get() {
        if (Array.isArray(this.modelValue)) return this.modelValue;

        return Number(this.modelValue);
      },
      set(value) {
        this.$emit("update:modelValue", value || null);
      },
    },
    multipleFiltersSelected() {
      return (
        this.multiple && this.selectedValue && this.selectedValue.length > 1
      );
    },
    displayValue() {
      if (!this.multiple || !this.selectedValue || !this.selectedValue.length) {
        return null;
      }

      return `${this.selectedValue.length} Selected`;
    },
    selectedFilterSets() {
      return this.filterSets.filter((set) =>
        this.selectedValue.includes(set.id)
      );
    },
    newFilterSetLink() {
      return `/organisations/${this.organisationId}/filter_sets/new`;
    },
    cssClasses() {
      return {
        "has-multiple": this.multipleFiltersSelected,
        "col-10": this.showNewFilterSetLink && !this.hideInfo,
        "col-11": !this.showNewFilterSetLink && !this.hideInfo,
        "col-12": this.hideInfo,
      };
    },
  },
  mounted() {
    if (!this.organisation?.id) {
      this.loadOrganisation();
    }

    this.loadFilterSets();
  },
  methods: {
    async loadOrganisation() {
      try {
        this.organisation = (
          await Organisation.select(["defaultFilterSetId", "name"]).find(
            this.organisationId
          )
        ).data;
      } catch (error) {
        this.$error("Error loading Organisation", error);
      }
    },
    async loadFilterSets() {
      try {
        const { data } = await this.$streemApiAdmin.get(
          `organisations/${this.organisationId}/filter_sets`
        );

        this.filterSets = data.map((filterSet) => ({
          ...filterSet,
          label:
            filterSet.id === Number(this.organisation.defaultFilterSetId)
              ? `${filterSet.label} (Default for ${this.organisation.name})`
              : filterSet.label,
        }));
      } catch {
        this.$q.notify("There was an issue loading filter sets");
      }
    },
    removeFilterSet(set) {
      const index = this.selectedValue.findIndex((id) => id === set.id);
      this.selectedValue.splice(index, 1);
    },
    viewFilterSet(filterSet) {
      this.$modals.open("ViewFilterSetModal", {
        props: { filterSet },
      });
    },
  },
};
</script>

<style lang="scss" scoped>
.has-multiple {
  border: 2px solid $radio;
}
</style>
